import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { User, AuthContextType, LoginResponse, formatErrorMessage } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

  // Configure axios defaults
  axios.defaults.baseURL = API_BASE_URL;
  // Note: withCredentials is not needed since we're using Authorization headers, not cookies

  // Add request interceptor to include auth token
  axios.interceptors.request.use(
    (config) => {
      // Don't add access token to refresh token requests
      if (!config.url?.includes('/auth/refresh')) {
        const token = Cookies.get('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle token refresh
  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // Don't retry refresh endpoint calls to prevent infinite loops
      if (originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        // Prevent multiple simultaneous refresh attempts
        if (isRefreshing) {
          return Promise.reject(error);
        }

        try {
          const refreshToken = Cookies.get('refresh_token');
          if (refreshToken) {
            console.log('Attempting token refresh with token:', refreshToken.substring(0, 20) + '...');
            setIsRefreshing(true);
            console.log('Making refresh request with token:', refreshToken.substring(0, 20) + '...');
            const response = await axios.post<LoginResponse>('/auth/refresh', {}, {
              headers: {
                Authorization: `Bearer ${refreshToken}`
              }
            });
            console.log('Refresh request successful, received new tokens');

            const { access_token, refresh_token: newRefreshToken } = response.data;
            Cookies.set('access_token', access_token, { 
              expires: 1/48, // 30 minutes
              secure: process.env.NODE_ENV === 'production',
              sameSite: 'strict'
            });
            Cookies.set('refresh_token', newRefreshToken, { 
              expires: 7, // 7 days
              secure: process.env.NODE_ENV === 'production',
              sameSite: 'strict'
            });

            // Retry the original request
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            setIsRefreshing(false);
            return axios(originalRequest);
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          if (refreshError instanceof AxiosError) {
            console.error('Refresh error response:', refreshError.response?.data);
          }
          setIsRefreshing(false);
          // Refresh failed, logout user
          logout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await axios.post<LoginResponse>('/auth/login', { email, password });
      const { access_token, refresh_token } = response.data;

      // Store tokens in cookies with secure settings
      Cookies.set('access_token', access_token, { 
        expires: 1/48, // 30 minutes
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      Cookies.set('refresh_token', refresh_token, { 
        expires: 7, // 7 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });

      // Get user info
      const userResponse = await axios.get<User>('/auth/me');
      setUser(userResponse.data);
      setIsAuthenticated(true);

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatErrorMessage(error, 'Login failed')
      };
    }
  };

  const signup = async (email: string, username: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      await axios.post('/auth/register', { email, username, password });
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: formatErrorMessage(error, 'Signup failed')
      };
    }
  };

  const logout = (): void => {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  const checkAuth = useCallback(async (): Promise<void> => {
    try {
      const token = Cookies.get('access_token');
      const refreshToken = Cookies.get('refresh_token');
      
      console.log('Auth check - Access token exists:', !!token);
      console.log('Auth check - Refresh token exists:', !!refreshToken);
      
      if (token) {
        try {
          const response = await axios.get<User>('/auth/me');
          setUser(response.data);
          setIsAuthenticated(true);
          console.log('Auth check - User authenticated with access token');
          return;
        } catch (error) {
          console.log('Auth check - Access token invalid, trying refresh');
        }
      }
      
      // Token is invalid or doesn't exist, try to refresh
      if (refreshToken && !isRefreshing) {
        try {
          console.log('Auth check - Attempting token refresh with token:', refreshToken.substring(0, 20) + '...');
          setIsRefreshing(true);
          console.log('Auth check - Making refresh request with token:', refreshToken.substring(0, 20) + '...');
          const response = await axios.post<LoginResponse>('/auth/refresh', {}, {
            headers: {
              Authorization: `Bearer ${refreshToken}`
            }
          });
          console.log('Auth check - Refresh request successful, received new tokens');

          const { access_token, refresh_token: newRefreshToken } = response.data;
          Cookies.set('access_token', access_token, { 
            expires: 1/48, // 30 minutes
            secure: process.env.NODE_ENV === 'production',
            sameSite: 'strict'
          });
          Cookies.set('refresh_token', newRefreshToken, { 
            expires: 7, // 7 days
            secure: process.env.NODE_ENV === 'production',
            sameSite: 'strict'
          });

          const userResponse = await axios.get<User>('/auth/me');
          setUser(userResponse.data);
          setIsAuthenticated(true);
          console.log('Auth check - User authenticated with refresh token');
        } catch (refreshError) {
          console.error('Auth check - Refresh failed:', refreshError);
          if (refreshError instanceof AxiosError) {
            console.error('Auth check - Refresh error response:', refreshError.response?.data);
          }
          logout();
        } finally {
          setIsRefreshing(false);
        }
      } else if (!refreshToken) {
        console.log('Auth check - No tokens available');
        logout();
      }
    } catch (error) {
      console.log('Auth check - Error:', error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  const getToken = (): string | undefined => {
    return Cookies.get('access_token');
  };

  useEffect(() => {
    checkAuth();
    
    // Set up periodic token refresh (every 25 minutes)
    const refreshInterval = setInterval(() => {
      const refreshToken = Cookies.get('refresh_token');
      if (refreshToken && isAuthenticated) {
        console.log('Periodic token refresh triggered');
        refreshAccessToken();
      }
    }, 25 * 60 * 1000); // 25 minutes
    
    return () => clearInterval(refreshInterval);
  }, [checkAuth, isAuthenticated]);
  
  const refreshAccessToken = async (): Promise<void> => {
    try {
      const refreshToken = Cookies.get('refresh_token');
      if (!refreshToken || isRefreshing) return;
      
      setIsRefreshing(true);
      const response = await axios.post<LoginResponse>('/auth/refresh', {}, {
        headers: {
          Authorization: `Bearer ${refreshToken}`
        }
      });

      const { access_token, refresh_token: newRefreshToken } = response.data;
      Cookies.set('access_token', access_token, { 
        expires: 1/48, // 30 minutes
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      Cookies.set('refresh_token', newRefreshToken, { 
        expires: 7, // 7 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      });
      
      console.log('Token refreshed successfully');
    } catch (error) {
      console.error('Periodic token refresh failed:', error);
      if (error instanceof AxiosError) {
        console.error('Periodic refresh error response:', error.response?.data);
      }
      logout();
    } finally {
      setIsRefreshing(false);
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    signup,
    logout,
    token: getToken()
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
